export const COPILOT_API_URL =
  import.meta.env.VITE_COPILOT_API_URL || 'http://localhost:3100'

export const COPILOT_CHAT_ENDPOINT = `${COPILOT_API_URL}/api/chat`

export const COPILOT_SETTINGS_STORAGE_KEY = 'cisgenx.copilot.widgetSettings'

export const COPILOT_MESSAGES_STORAGE_KEY = 'cisgenx.copilot.messages'

export const COPILOT_CURRENT_CONVERSATION_KEY =
  'cisgenx.copilot.currentConversationId'

export type CopilotCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

export interface CopilotSettings {
  corner: CopilotCorner
  offsetX: number
  offsetY: number
  width: number
  height: number
  // Full-screen toggle persisted across sessions. Optional for backward
  // compatibility with previously-stored settings that omit this key.
  expanded?: boolean
}

// Margin (px) applied on each side of the viewport when the copilot panel
// is in full-screen mode so the surrounding chrome is still visible.
export const COPILOT_EXPANDED_MARGIN = 16

// Gap between the FAB and the docked panel edge along the anchored axis.
// Kept in sync with buildPanelAnchor() in CopilotWidget so max-size
// calculations can account for the space reserved for the FAB.
export const COPILOT_PANEL_GAP = 72

// Min size matches DEFAULT_COPILOT_SETTINGS so drag-resize cannot shrink the
// panel below the default footprint. Max size is computed at runtime from the
// viewport (window inner size minus COPILOT_EXPANDED_MARGIN on each side).
export const COPILOT_SIZE_BOUNDS = {
  minWidth: 400,
  minHeight: 600,
} as const

// Panel grows away from its anchored corner; the anchored side is at
// `offsetX` (horizontal) / `offsetY + COPILOT_PANEL_GAP` (vertical) from the
// viewport edge. The opposite side must stay COPILOT_EXPANDED_MARGIN away
// from the far viewport edge so the panel never clips the browser chrome.
export const getCopilotMaxWidth = (offsetX: number = 0): number =>
  Math.max(
    COPILOT_SIZE_BOUNDS.minWidth,
    window.innerWidth - offsetX - COPILOT_EXPANDED_MARGIN,
  )

export const getCopilotMaxHeight = (offsetY: number = 0): number =>
  Math.max(
    COPILOT_SIZE_BOUNDS.minHeight,
    window.innerHeight - offsetY - COPILOT_PANEL_GAP - COPILOT_EXPANDED_MARGIN,
  )

export const COPILOT_SIZE_PRESETS: {
  label: string
  width: number
  height: number
}[] = [
  { label: 'S', width: 360, height: 520 },
  { label: 'M', width: 400, height: 600 },
  { label: 'L', width: 480, height: 680 },
  { label: 'XL', width: 560, height: 760 },
]

export const DEFAULT_COPILOT_SETTINGS: CopilotSettings = {
  corner: 'bottom-right',
  offsetX: 24,
  offsetY: 24,
  width: 400,
  height: 600,
  expanded: false,
}

export const COPILOT_CORNER_OPTIONS: { value: CopilotCorner; label: string }[] =
  [
    { value: 'bottom-right', label: 'Bottom right' },
    { value: 'bottom-left', label: 'Bottom left' },
    { value: 'top-right', label: 'Top right' },
    { value: 'top-left', label: 'Top left' },
  ]
